import { I18N } from 'aurelia-i18n';
import { mock, instance, when, anything } from 'ts-mockito';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AccountPaymentCards } from './account-payment-cards';
import { State } from '../../../services/global-state';
import { Business } from '../../../models/business';
import { PayrazrService } from '../../../services/payrazr';
import { Account } from '../../../models/customer';

describe('NewPortalWeb', () => {
	describe('pages', () => {
		describe('customer-dashboard', () => {
			describe('account-payment-cards', () => {
				let component: AccountPaymentCards;
				let stubState: State;
				const mockBusiness: Business = mock(Business);
				const mockPayrazrService: PayrazrService = mock(PayrazrService);
				const mockI18N: I18N = mock(I18N);
				const mockEventAggregator: EventAggregator = mock(EventAggregator);
                const mockAccountOne: Account = mock(Account);
                const mockAccountTwo: Account = mock(Account);

                const minPayment = 1;
                const maxPayment = 9999;

                const portfolioName = 'Test Portfolio';

                let portalSettings = {
					portfolioKey: 999999,
					name: portfolioName,
					settings: {
						paymentTypes: {
                            allowConsumerToCancelPaymentPlan: true,
                            allowConsumersToCancelSingleFutureOnPortal: false,
                            allowConsumersToSkipAPayment: true,
                            allowFuture: true,
                            allowInstallments: true,
                            allowRecurring: false,
                            allowRehab: false,
                            allowSingle: true,
                            enableMinimumPaymentDueOnAccount: false,
							installments: {
                                numberOfRetriesForCardDeclines: 0,
								maxFirstInstallmentFutureDays: 10,
                                balanceRanges: [
                                    {
                                        enableInstallments: true,
                                        enableNegotiator: true,
                                        installments : [
                                            {number: 2, discountPercent: 10},
                                            {number: 7, discountPercent: 2}
                                        ],
                                        max: 100,
                                        min: 1,
                                        payInFull: true,
                                        payInFullDiscountPercent: 5,
                                    },
                                    {
                                        enableInstallments: true,
                                        enableNegotiator: true,
                                        installments : [
                                            {number: 2, discountPercent: 8},
                                            {number: 3, discountPercent: 2}
                                        ],
                                        max: 150,
                                        min: 101,
                                        payInFull: true,
                                        payInFullDiscountPercent: 0,
                                    },
                                    {
                                        enableInstallments: true,
                                        enableNegotiator: true,
                                        installments : [
                                            {number: 5, discountPercent: 3},
                                            {number: 9, discountPercent: 1}
                                        ],
                                        max: 200,
                                        min: 151,
                                        payInFull: true,
                                        payInFullDiscountPercent: 3,
                                    }
                                ]
							},
						},
                        paymentMethods: {
                            allowCreditCard: true,
                            allowAch: true
                        },
                        minimumPaymentAmount: minPayment,
                        maximumPaymentAmount: maxPayment
					},
					autopaySettings: {
                        enableAutopay: false
                    }
				};

                const i18next = {
                    t: (a, b) => a
                };

				beforeEach(() => {
					stubState = new State(
						instance(mockI18N),
						instance(mockEventAggregator)
					);

                    stubState.business = instance(mockBusiness);
                    portalSettings.settings.maximumPaymentAmount = maxPayment;
                    portalSettings.settings.minimumPaymentAmount = minPayment;
                    portalSettings.settings.paymentMethods.allowAch = true;
                    portalSettings.settings.paymentMethods.allowCreditCard = true;
                    
					when(mockI18N.i18next).thenReturn(i18next);
					stubState.business = instance(mockBusiness);

					component = new AccountPaymentCards(
						stubState,
                        instance(mockPayrazrService),
                        instance(mockI18N)
					);

                    mockAccountOne.portfolioName = portfolioName;
                    mockAccountOne.balance = 10;
                    mockAccountTwo.portfolioName = portfolioName;
                    mockAccountTwo.balance = 10;

                    component.selectedAccounts = [];
                    component.selectedAccounts.push(mockAccountOne);
				});

				it('should initialize AccountPaymentCards component', () => {
					expect(component).toBeTruthy();
				});

                describe('attached()', () => {

                    it('should set hasFocus as true', () => {
                        component.selectedAccounts = [];

                        component.attached();

                        expect(component.hasFocus).toBeTrue();
                    });

                    it('should call getAvailableCards() and set hasFocus as true', () => {

                        spyOn(component, 'getAvailableCards').and.stub();

                        component.attached();

                        expect(component.getAvailableCards).toHaveBeenCalled();
                        expect(component.hasFocus).toBeTrue();
                    });

                });

                describe('selectedAccountsChanged()', () => {

                    it('should call getAvailableCards(), set hasPlan and cachedSelectedAccountsSettings as undefined', () => {

                        spyOn(component, 'getAvailableCards').and.stub();

                        component.selectedAccountsChanged(anything(), anything());

                        expect(component.hasPlan).toBeUndefined();
                        expect(component.cachedSelectedAccountsSettings).toBeUndefined();
                        expect(component.getAvailableCards).toHaveBeenCalled();
                    });

                });

                describe('getAvailableCards()', () => {

                    it('should set error when max payment amount is lower than min payment amount', () => {

                        portalSettings.settings.maximumPaymentAmount = 0;
                        portalSettings.settings.minimumPaymentAmount = 1;

                        spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                        spyOn(i18next, 't').and.returnValue('Selected accounts have incompatible payment requirements.');

                        component.getAvailableCards();

                        expect(component.cards.length).toBe(0);
                        expect(component.error).toBe('Selected accounts have incompatible payment requirements.');
                    });

                    it('should set error when payment method is not valid', () => {

                        portalSettings.settings.paymentMethods.allowAch = false;
                        portalSettings.settings.paymentMethods.allowCreditCard = false;

                        spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                        spyOn(i18next, 't').and.returnValue('Selected accounts have incompatible payment requirements.');

                        component.getAvailableCards();

                        expect(component.cards.length).toBe(0);
                        expect(component.error).toBe('Selected accounts have incompatible payment requirements.');
                    });

                    it('should iterate showCardAutopayEdit all cards options and display available cards', () => {

                        spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);

                        spyOn(component, 'showCardPayInFull').and.returnValue(true);
                        spyOn(component, 'showCardSingle').and.returnValue(true);
                        spyOn(component, 'showCardInstallmentSetup').and.returnValue(false);
                        spyOn(component, 'showCardInstallmentEdit').and.returnValue(true);
                        spyOn(component, 'showCardNegotiator').and.returnValue(true);
                        spyOn(component, 'showCardRecurringSetup').and.returnValue(false);
                        spyOn(component, 'showCardRecurringEdit').and.returnValue(true);
                        spyOn(component, 'showCardAutopaySetup').and.returnValue(false);
                        spyOn(component, 'showCardAutopayEdit').and.returnValue(true);

                        component.getAvailableCards();

                        expect(component.cards.length).toBe(6);
                    });

                });

                describe('showCardInstallmentSetup()', () => {

                    describe('Single Account Payment Plan (SAPP)', () => {

                        it('should hide card installment setup when no positive balance is present', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = true;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'hasPositiveBalance').and.returnValue(false);
    
                            var showCard = component.showCardInstallmentSetup();    
                            
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(showCard).toBeFalse();
                        });

                        it('should hide card installment setup when selected accounts settings does not allow installments', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = false;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
    
                            var showCard = component.showCardInstallmentSetup();
    
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(showCard).toBeFalse();

                        });

                        it('should hide card installment setup when an active plan already exists', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = true;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
                            spyOn(component, 'hasActivePlan').and.returnValue(true);
    
                            var showCard = component.showCardInstallmentSetup();
    
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(component.hasActivePlan).toHaveBeenCalled();
                            expect(showCard).toBeFalse();                            
                        });

                        it('should hide card installment setup when account balance it is not within balance ranges', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = true;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
                            spyOn(component, 'hasActivePlan').and.returnValue(false);
                            spyOn(component, 'isMappAllowed').and.returnValue(false);

                            component.selectedAccounts[0].balance = Number.MAX_VALUE;
    
                            var showCard = component.showCardInstallmentSetup();
    
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(component.hasActivePlan).toHaveBeenCalled();
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(showCard).toBeFalse();
                        });

                        it('should show card installment setup when account balance it is within balance ranges', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = true;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
                            spyOn(component, 'hasActivePlan').and.returnValue(false);
                            spyOn(component, 'isMappAllowed').and.returnValue(false);

                            component.selectedAccounts[0].balance = 10;
    
                            var showCard = component.showCardInstallmentSetup();
    
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(component.hasActivePlan).toHaveBeenCalled();
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(showCard).toBeTrue();
                        });

                    });

                    describe('Multi Account Payment Plan (MAPP)', () => {

                        beforeEach(() => {
                            mockAccountTwo.balance = 1;
                            component.selectedAccounts.push(mockAccountTwo);
                        });

                        afterEach(() => {
                            component.selectedAccounts.pop();
                        });

                        it('should hide card installment setup when MAPP is not allowed ', () => {
                            
                            spyOn(component, 'isMappAllowed').and.returnValue(false);
    
                            var showCard = component.showCardInstallmentSetup();    
                            
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(showCard).toBeFalse();
                        });

                        it('should hide card installment setup when no positive balance is present', () => {
                            
                            spyOn(component, 'isMappAllowed').and.returnValue(true);
                            spyOn(component, 'hasPositiveBalance').and.returnValue(false);
    
                            var showCard = component.showCardInstallmentSetup();    
                            
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(showCard).toBeFalse();
                        });

                        it('should hide card installment setup when selected account settings does not allow installments', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = false;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'isMappAllowed').and.returnValue(true);
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
    
                            var showCard = component.showCardInstallmentSetup();    
                            
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(showCard).toBeFalse();
                        });

                        it('should hide card installment setup when an active plan already exists for the selected accounts', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = true;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'isMappAllowed').and.returnValue(true);
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
                            spyOn(component, 'hasActivePlan').and.returnValue(true);
    
                            var showCard = component.showCardInstallmentSetup();    
                            
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(component.hasActivePlan).toHaveBeenCalled();
                            expect(showCard).toBeFalse();
                        });

                        it('should hide card installment setup when portfolio name is not the same on selected accounts', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = true;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'isMappAllowed').and.returnValue(true);
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
                            spyOn(component, 'hasActivePlan').and.returnValue(false);

                            component.selectedAccounts[1].portfolioName = `${portfolioName}_${new Date().getTime()}`;
    
                            var showCard = component.showCardInstallmentSetup();    
                            
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(component.hasActivePlan).toHaveBeenCalled();
                            expect(showCard).toBeFalse();
                        });

                        it('should hide card installment setup when the balance of selected accounts does not match balance ranges', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = true;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'isMappAllowed').and.returnValue(true);
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
                            spyOn(component, 'hasActivePlan').and.returnValue(false);

                            component.selectedAccounts[0].balance = Number.MAX_VALUE;
    
                            var showCard = component.showCardInstallmentSetup();    
                            
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(component.hasActivePlan).toHaveBeenCalled();
                            expect(showCard).toBeFalse();
                        });

                        it('should show card installment setup when the balance of selected accounts it is within balance ranges', () => {

                            portalSettings.settings.paymentTypes.allowInstallments = true;

                            spyOn(stubState.business, 'fetchPortfolioSettings').and.callFake(() => portalSettings);
                            
                            spyOn(component, 'isMappAllowed').and.returnValue(true);
                            spyOn(component, 'hasPositiveBalance').and.returnValue(true);
                            spyOn(component, 'hasActivePlan').and.returnValue(false);
    
                            var showCard = component.showCardInstallmentSetup();
                            
                            expect(component.isMappAllowed).toHaveBeenCalled();
                            expect(component.hasPositiveBalance).toHaveBeenCalled();
                            expect(component.hasActivePlan).toHaveBeenCalled();
                            expect(showCard).toBeTrue();
                        });

                    });

                });

			});
		});
	});
});
